export const TELEMETRY_SERVICES = [
  "web",
  "api",
  "game-server",
  "preset-server",
  "asset-editor",
  "agent-runner",
  "agent",
  "telemetry-tail",
  "tiles",
] as const;
export type TelemetryService = (typeof TELEMETRY_SERVICES)[number];

export type LogLevel = "debug" | "info" | "warn" | "error";

export const TRACEPARENT_HEADER = "traceparent";

/** The W3C header that carries key-values along with a trace to the next service. */
export const BAGGAGE_HEADER = "baggage";

/** A W3C `baggage` header value holding `entries`. */
export function formatBaggage(entries: Record<string, string>): string {
  return Object.entries(entries)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join(",");
}

/** The entries of a W3C `baggage` header value, without their properties. */
export function readBaggage(value: string | null | undefined): Record<string, string> {
  const entries: Record<string, string> = {};
  for (const member of value?.split(",") ?? []) {
    const [pair = ""] = member.split(";");
    const separator = pair.indexOf("=");
    if (separator <= 0) continue;
    try {
      entries[pair.slice(0, separator).trim()] = decodeURIComponent(
        pair.slice(separator + 1).trim(),
      );
    } catch {}
  }
  return entries;
}

/** A W3C `traceparent` header value: version, trace id, span id, flags. */
export const TRACEPARENT_PATTERN = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/;

export interface SpanContext {
  traceId: string;
  spanId: string;
  sampled: boolean;
}

const allZero = (hex: string) => /^0+$/.test(hex);

function randomHex(bytes: number): string {
  let value: string;
  do {
    const buffer = new Uint8Array(bytes);
    crypto.getRandomValues(buffer);
    value = [...buffer].map(byte => byte.toString(16).padStart(2, "0")).join("");
  } while (allZero(value));
  return value;
}

export const newTraceId = () => randomHex(16);
export const newSpanId = () => randomHex(8);

export function parseTraceparent(value: string | null | undefined): SpanContext | undefined {
  const match = value ? TRACEPARENT_PATTERN.exec(value.trim()) : null;
  if (!match) return;
  const [, traceId, spanId, flags] = match;
  if (!traceId || !spanId || !flags || allZero(traceId) || allZero(spanId)) return;
  return { traceId, spanId, sampled: (Number.parseInt(flags, 16) & 1) === 1 };
}

export function formatTraceparent({ traceId, spanId, sampled }: SpanContext): string {
  return `00-${traceId}-${spanId}-${sampled ? "01" : "00"}`;
}

/** The standard `exception` event for an error, and its class as the span's `error.type`. */
export function exceptionEvent(error: unknown, timeMs = Date.now()): SpanEvent {
  return {
    name: "exception",
    timeMs,
    attributes: {
      "exception.type": errorType(error),
      "exception.message": error instanceof Error ? error.message : String(error),
      ...(error instanceof Error && error.stack ? { "exception.stacktrace": error.stack } : {}),
    },
  };
}

export function errorType(error: unknown): string {
  return error instanceof Error ? error.name : typeof error;
}

export type SpanAttribute = string | number | boolean | string[] | number[] | boolean[];
export type SpanAttributes = Record<string, SpanAttribute>;
export type SpanKind = "server" | "client" | "internal" | "consumer" | "producer";

export interface SpanLink {
  traceId: string;
  spanId: string;
  attributes?: SpanAttributes;
}

/** Something that happened at one moment during a span, such as the exception that ended it. */
export interface SpanEvent {
  name: string;
  timeMs: number;
  attributes?: SpanAttributes;
}

/** The key that marks a console line as a finished span. */
export const SPAN_LINE_KEY = "pax.span";

/** One finished span, written by a Worker as a single console line. */
export interface SpanLine {
  service: TelemetryService;
  /** The build that wrote it, filled in by whoever exports it. */
  serviceVersion?: string;
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  name: string;
  kind: SpanKind;
  startMs: number;
  endMs: number;
  status?: { code: "error"; message?: string };
  attributes: SpanAttributes;
  events?: SpanEvent[];
  links?: SpanLink[];
}

/** The key that marks a console line as a product event bound for ClickHouse. */
export const EVENT_LINE_KEY = "pax.event";

/**
 * One notable moment, such as a completed round, written by a Worker as a single console line.
 * The telemetry tail stores it in ClickHouse; its fields are the event's own, kept small.
 */
export interface EventLine {
  eventId: string;
  type: string;
  occurredAt: string;
  [field: string]: unknown;
}

/** One structured log line. Fields other than these are attributes. */
export interface LogLine {
  ts: string;
  level: LogLevel;
  msg: string;
  service: TelemetryService;
  serviceVersion?: string;
  traceId?: string;
  spanId?: string;
  [field: string]: unknown;
}
